/**
 * Fetch wrapper with retry, JSON serialization, and the V3 auth-header
 * priority (CONTRACTS §C1).
 *
 *  1. "tma <initData>"     when running inside Telegram (has initData)
 *  2. "token <id>:<token>"  when a local auth token is cached
 *  3. "dev <staffId>"       only in DEV_MODE; staffId read from ?dev= query
 *                           param (sticky into localStorage) or directly
 *                           from localStorage["ub_dev_staff_id"]
 *
 * Errors are surfaced as `ApiException` with the HTTP status + parsed body.
 */

import { getInitData, isInTelegram } from "../lib/telegram";
import { ApiException, type ApiError } from "./types";

const DEV_KEY = "ub_dev_staff_id";
const TOKEN_KEY = "ub_auth_token";

/** Stash the `?dev=<id>` query param into localStorage so refreshes keep working. */
export function captureDevQueryParam(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const dev = url.searchParams.get("dev");
  if (dev) {
    localStorage.setItem(DEV_KEY, dev);
    url.searchParams.delete("dev");
    window.history.replaceState(null, "", url.toString());
  }
}

/** «android 8.0» — платформа и версия Telegram-клиента для журнала входов (13.10). Вне Telegram — пусто. */
const uaClient: string = (() => {
  const w = typeof window !== "undefined" ? (window as unknown as { Telegram?: { WebApp?: { platform?: string; version?: string } } }).Telegram?.WebApp : undefined;
  return w?.platform && w.platform !== "unknown" ? `${w.platform} ${w.version ?? ""}`.trim().slice(0, 60) : "";
})();

function buildAuthHeader(): string | null {
  if (isInTelegram()) return `tma ${getInitData()}`;
  const token = localStorage.getItem(TOKEN_KEY); // "<staffId>:<token>"
  if (token) return `token ${token}`;
  const dev = localStorage.getItem(DEV_KEY);
  if (dev) return `dev ${dev}`;
  return null;
}

interface ApiOptions extends Omit<RequestInit, "body" | "headers"> {
  /**
   * Тело запроса. Обычно — объект, его сериализуем сами.
   *
   * Строку и FormData отдаём КАК ЕСТЬ. Это не косметика: привычная по fetch запись
   * `body: JSON.stringify({...})` иначе сериализовалась ВТОРОЙ раз, сервер получал
   * JSON-строку вместо объекта, все поля читались как undefined и запрос падал в 400.
   * Так сломались номинирование в ЮСКАРе, привязка игры и правка монтажей — молча,
   * потому что вызывающий код глушил ошибку пустым catch.
   */
  body?: unknown;
  /** Override headers; merged on top of the defaults. */
  headers?: Record<string, string>;
  /** Override retry count. Default: 3 for GET, 2 for everything else. */
  retries?: number;
  /** Override base URL — defaults to "" (relative; Vite proxies /api → :8787). */
  baseUrl?: string;
}

const DEFAULT_RETRY_DELAYS_MS = [600, 1200, 2400];

/**
 * Build-time API origin. Dev → "" (Vite proxies /api → :8787). Staging/prod →
 * VITE_API_URL (the deployed worker URL), injected by the deploy script so the
 * Pages site reaches its API worker cross-origin.
 */
const API_BASE: string = ((import.meta.env?.VITE_API_URL as string | undefined) ?? "").replace(/\/$/, "");

/**
 * Typed API call. Throws ApiException on non-2xx.
 *
 * Examples:
 *   const me = await api<MeResponse>("/api/me");
 *   await api("/api/bookings", { method: "POST", body: { … } });
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, headers, retries, baseUrl = API_BASE, ...rest } = opts;
  const method = (rest.method ?? "GET").toUpperCase();
  /* Мутации по умолчанию НЕ ретраим (24.08: авто-повтор зависшего PUT /move
     дублировал TG-рассылку) — где повтор безопасен, передавайте retries явно. */
  const maxRetries = retries ?? (method === "GET" ? 3 : 0);

  const auth = buildAuthHeader();
  const reqHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(body !== undefined && !(body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
    ...(auth ? { Authorization: auth } : {}),
    ...(uaClient ? { "X-UB-Client": uaClient } : {}),   // 13.10: устройство для журнала входов
    ...(headers ?? {}),
  };

  const init: RequestInit = {
    ...rest,
    method,
    headers: reqHeaders,
    /* Строка и FormData — уже готовое тело, второй раз не сериализуем (см. ApiOptions.body). */
    ...(body !== undefined
      ? { body: body instanceof FormData || typeof body === "string" ? body : JSON.stringify(body) }
      : {}),
  };

  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(`${baseUrl}${path}`, init);
      const text = await res.text();
      const parsed: unknown = text ? safeJsonParse(text) : null;

      if (!res.ok) {
        // 5xx and 429 are retryable; 4xx (except 429) are not
        const retryable = res.status >= 500 || res.status === 429;
        if (retryable && attempt < maxRetries) {
          await sleep(DEFAULT_RETRY_DELAYS_MS[attempt] ?? 2400);
          continue;
        }
        throw new ApiException(res.status, parsed as ApiError | null);
      }

      return parsed as T;
    } catch (err) {
      lastErr = err;
      if (err instanceof ApiException) throw err;
      if (err instanceof Error && err.name === "AbortError") throw err; // отменённый запрос не ретраим
      // Network errors — retry
      if (attempt < maxRetries) {
        await sleep(DEFAULT_RETRY_DELAYS_MS[attempt] ?? 2400);
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Защищённый файл (фото вейвера и т.п.) → objectURL: <img> не умеет слать
 *  Authorization, поэтому качаем fetch'ем сами (27.08). Отдаваемый URL надо
 *  освобождать URL.revokeObjectURL, когда картинка больше не нужна. */
export async function apiFileUrl(path: string): Promise<string> {
  const auth = buildAuthHeader();
  const res = await fetch(`${API_BASE}${path}`, { headers: { ...(auth ? { Authorization: auth } : {}), ...(uaClient ? { "X-UB-Client": uaClient } : {}) } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
}
